import { atom, useAtom, useSetAtom } from 'jotai';
import React from 'react';

import type * as api from '@gitbook/api-client';

export type SiteUpgradeModalState = {
    tab: 'plans';
    type?: api.SiteType;
    reason: api.SiteUpgradeReason;
};

interface SiteUpgradeModalAPI {
    upgradeState: SiteUpgradeModalState | null;
    site: api.Site | null;
    isOpened: boolean;
    open: (site: api.Site, state: SiteUpgradeModalState) => void;
    close: () => void;
}

/**
 * State for the site upgrade modal.
 */
const siteUpgradeModalStateAtom = atom<SiteUpgradeModalState | null>(null);

/**
 * Modal state, separated from `upgradeState` so UI relying on upgradeState is not affected.
 */
const modalVisibilityAtom = atom<api.Site | null>(null);

export function useSiteUpgradeModal(): SiteUpgradeModalAPI {
    const [upgradeState, setUpgradeState] = useAtom(siteUpgradeModalStateAtom);
    const [site, setModalSite] = useAtom(modalVisibilityAtom);

    return React.useMemo<SiteUpgradeModalAPI>(
        () => ({
            upgradeState,
            isOpened: Boolean(site),
            site,
            open: (site, newState) => {
                setUpgradeState(newState);
                setModalSite(site);
            },
            close: () => {
                setModalSite(null);
            },
        }),
        [upgradeState, site, setUpgradeState, setModalSite]
    );
}

/**
 * Open the site upgrade modal without subscribing to its state, for callers that never read it.
 */
export function useOpenSiteUpgradeModal(): SiteUpgradeModalAPI['open'] {
    const setUpgradeState = useSetAtom(siteUpgradeModalStateAtom);
    const setModalSite = useSetAtom(modalVisibilityAtom);

    return React.useCallback(
        (site, newState) => {
            setUpgradeState(newState);
            setModalSite(site);
        },
        [setUpgradeState, setModalSite]
    );
}
